class CartDrawerSection extends HTMLElement {
  cartUpdateUnsubscriber = undefined;
  constructor() {
    super();

    this.cartType = this.dataset.cartType;
    this.drawerClass = "wt-cart__drawer";
    this.drawer = this.querySelector(`.${this.drawerClass}`);
    this.classDrawerActive = `${this.drawerClass}--open`;
    this.pageOverlayClass = "page-overlay-cart";
    this.activeOverlayBodyClass = `${this.pageOverlayClass}-on`;
    this.body = document.body;
    this.triggerQuery = [
      `.wt-cart__trigger`,
      `.wt-cart__back-link`,
      `.${this.pageOverlayClass}`,
    ].join(", ");
    this.triggers = () => document.querySelectorAll(this.triggerQuery);
    this.isOpen = false;
    this.isCartPage = window.location.pathname === window.routes.cart_url;
    this.closeButton = () => this.querySelector(".wt-cart__drawer__close");
    this.mainTrigger = document.querySelector(".wt-cart__trigger");
    this.toggleEelements = () =>
      this.querySelectorAll(this.dataset.toggleTabindex);

    // Stores element that opened the drawer (for focus restore after close)
    this.openerEl = null;
  }

  connectedCallback() {
    if (this.cartType === "page" || this.isCartPage) {
      document.addEventListener("cart-drawer:refresh", (e) =>
        this.refreshCartDrawer(e),
      );
      return;
    }

    this.init();
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
      if (this.isOpen) {
        setTabindex(this.toggleEelements(), "0");
        this.closeButton()?.focus();
      }
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  rememberOpener(e) {
    const current = e?.currentTarget;
    const active = document.activeElement;
    const interactive =
      current instanceof HTMLElement
        ? current.closest?.("button, a, [tabindex], input, select") || current
        : null;
    this.openerEl =
      interactive instanceof HTMLElement
        ? interactive
        : active instanceof HTMLElement
          ? active
          : null;
  }

  restoreFocusToOpener() {
    const el = this.openerEl;
    if (!el) return;
    if (!document.contains(el)) return;
    const isDisabled =
      el.hasAttribute("disabled") ||
      el.getAttribute("aria-disabled") === "true";
    if (isDisabled) return;
    requestAnimationFrame(() => {
      el.focus?.();
    });
  }

  getFocusableElements() {
    const focusableElementsSelector =
      "button, [href], input, select, [tabindex]";
    const focusableElements = () =>
      Array.from(this.querySelectorAll(focusableElementsSelector)).filter(
        (el) => !el.hasAttribute("disabled") && el.tabIndex >= 0,
      );
    return {
      focusableElements,
      first: focusableElements()[0],
      last: focusableElements()[focusableElements().length - 1],
    };
  }

  temporaryHideFocusVisible() {
    document.body.classList.add("no-focus-visible");
  }

  // The three open-state signals below must always move together. A throw in
  // the middle of this method used to leave the backdrop up with the panel
  // shut, which on a phone hides the whole page until a reload, so every call
  // that can fail is now optional.
  onToggle() {
    if (this.hasAttribute("open")) {
      this.removeAttribute("open");
      this.isOpen = false;
      this.temporaryHideFocusVisible();
      setTabindex(this.toggleEelements(), "-1");
      this.restoreFocusToOpener();
    } else {
      this.setAttribute("open", "");
      this.isOpen = true;
      this.closeButton()?.focus();
      this.temporaryHideFocusVisible();
      setTabindex(this.toggleEelements(), "0");
    }
  }

  toggleDrawerClasses() {
    // Class state first, focus bookkeeping second: if anything in onToggle
    // throws, the drawer and the backdrop must not be left disagreeing.
    const willOpen = !this.hasAttribute("open");

    if (this.drawer) this.drawer.classList.toggle(this.classDrawerActive, willOpen);
    this.body.classList.toggle(this.activeOverlayBodyClass, willOpen);

    this.onToggle();

    const eventName = this.isOpen
      ? PUB_SUB_EVENTS.cartDrawerOpen
      : PUB_SUB_EVENTS.cartDrawerClose;
    document.dispatchEvent(
      new CustomEvent(eventName, { bubbles: true }),
    );
  }

  init() {
    this.addEventListener("keydown", (e) => {
      const isTabPressed =
        e.key === "Tab" || e.keyCode === 9 || e.code === "Tab";
      const { first, last } = this.getFocusableElements();
      if (e.key === "Escape" || e.keyCode === 27 || e.code === "Escape") {
        if (this.isOpen) this.toggleDrawerClasses();
      }
      if (isTabPressed) {
        if (e.shiftKey && document.activeElement === first) {
          last?.focus();
          e.preventDefault();
        } else if (!e.shiftKey && document.activeElement === last) {
          first?.focus();
          e.preventDefault();
        }
      }
    });

    this.triggers().forEach((trigger) => {
      trigger.addEventListener("click", (e) => {
        e.preventDefault();
        if (!this.isOpen) this.rememberOpener(e);
        this.toggleDrawerClasses();
      });
    });

    this.addEventListener("click", (e) => {
      if (e.target.classList.contains("wt-cart__drawer__close")) {
        e.preventDefault();
        this.toggleDrawerClasses();
      }
    });

    document.addEventListener("cart-drawer:refresh", (e) =>
      this.refreshCartDrawer(e),
    );
  }

  // MODIFIED: Default changed to false — drawer does NOT auto-open on add to cart.
  // Toast notification is shown instead with the product name.
  //
  // A missing section or a missing target used to throw here and abandon the
  // repaint half done, which is one of the ways the drawer ended up needing a
  // reload. Each section is now written only when both ends are present, and a
  // "vc:cart:rendered" event fires afterwards so the drawer can be opened off
  // the real completion instead of a guessed delay.
  renderContents(parsedState, isClosedCart = false) {
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      if (!sectionElement) return;

      const sectionHTML = parsedState?.sections?.[section.id];
      if (!sectionHTML) return;

      const inner = this.getSectionInnerHTML(sectionHTML, section.selector);
      if (inner == null) return;

      sectionElement.innerHTML = inner;
    });

    document.dispatchEvent(
      new CustomEvent("vc:cart:rendered", { bubbles: true }),
    );

    if (isClosedCart) {
      setTimeout(() => {
        this.toggleDrawerClasses();
        if (this.isOpen) this.closeButton()?.focus();
      });
    } else {
      // Show toast notification with product name
      const productTitle =
        parsedState.items?.[0]?.product_title ||
        parsedState.items?.[0]?.title ||
        null;
      if (productTitle) {
        CartToast.show(productTitle);
      }
    }
  }

  getSectionInnerHTML(html, selector = ".shopify-section") {
    return new DOMParser()
      .parseFromString(html, "text/html")
      ?.querySelector(selector)?.innerHTML;
  }

  getSectionsToRender() {
    return [
      { id: "cart-drawer", selector: "#CartDrawer" },
      { id: "cart-icon-bubble" },
    ];
  }

  refreshCartDrawer(e) {
    const sectionsToRender = this.getSectionsToRender();
    fetch(
      `${window.Shopify.routes.root}?sections=${sectionsToRender[0].id},${sectionsToRender[1].id}`,
    )
      .then((response) => response.json())
      .then((response) => {
        this.renderContents({ sections: response }, false);
      })
      .catch((e) => console.log(e));
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define("cart-drawer", CartDrawerSection);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: "CartDrawer",
        section: "cart-drawer",
        selector: ".drawer__inner",
      },
      {
        id: "cart-icon-bubble",
        section: "cart-icon-bubble",
        selector: ".shopify-section",
      },
    ];
  }
}

customElements.define("cart-drawer-items", CartDrawerItems);

// ---------------------------------------------------------------------------
// CartToast — lightweight "added to cart" notification
// ---------------------------------------------------------------------------
const CartToast = (() => {
  const STYLE_ID = "cart-toast-styles";
  const TOAST_ID = "cart-toast";
  const DURATION = 3000; // ms before auto-dismiss
  let hideTimer = null;

  function injectStyles() {
    if (document.getElementById(STYLE_ID)) return;
    const style = document.createElement("style");
    style.id = STYLE_ID;
    style.textContent = `
      #cart-toast {
        position: fixed;
        bottom: 24px;
        left: 50%;
        transform: translateX(-50%) translateY(20px);
        background: #1a1a1a;
        color: #fff;
        padding: 13px 22px;
        border-radius: 8px;
        font-size: 14px;
        font-family: inherit;
        white-space: nowrap;
        max-width: 90vw;
        overflow: hidden;
        text-overflow: ellipsis;
        box-shadow: 0 4px 18px rgba(0,0,0,0.22);
        opacity: 0;
        pointer-events: none;
        z-index: 99999;
        transition: opacity 0.28s ease, transform 0.28s ease;
      }
      #cart-toast.cart-toast--visible {
        opacity: 1;
        transform: translateX(-50%) translateY(0);
        pointer-events: auto;
      }
      #cart-toast .cart-toast__icon {
        display: inline-block;
        margin-right: 8px;
        vertical-align: middle;
        position: relative;
        top: -1px;
      }
    `;
    document.head.appendChild(style);
  }

  function getToast() {
    let el = document.getElementById(TOAST_ID);
    if (!el) {
      el = document.createElement("div");
      el.id = TOAST_ID;
      el.setAttribute("role", "status");
      el.setAttribute("aria-live", "polite");
      document.body.appendChild(el);
    }
    return el;
  }

  function show(productTitle) {
    injectStyles();
    const toast = getToast();

    // Truncate long titles
    const maxLen = 48;
    const title =
      productTitle.length > maxLen
        ? productTitle.slice(0, maxLen).trimEnd() + "…"
        : productTitle;

    toast.innerHTML = `<span class="cart-toast__icon" aria-hidden="true">&#10003;</span>${title} added to your cart`;

    // Reset any running timer
    clearTimeout(hideTimer);
    toast.classList.remove("cart-toast--visible");

    // Force reflow so the transition fires even on repeated adds
    void toast.offsetWidth;

    toast.classList.add("cart-toast--visible");
    hideTimer = setTimeout(() => hide(toast), DURATION);
  }

  function hide(toast) {
    toast = toast || document.getElementById(TOAST_ID);
    if (toast) toast.classList.remove("cart-toast--visible");
  }

  return { show, hide };
})();
